import { useEffect, useState } from "react";
import { listDrops, getWhitelistEntry, getWalletMintCount } from "../api/nftdropsApi";
import { getTemplate } from "../api/atomicApi";
import { mintDrop } from "../api/actions";
import { logActivity } from "../api/activityLog";
import { useWallet } from "../wallet/WalletContext";
import type { AtomicTemplate, NftDrop } from "../types";
import { IpfsImage } from "../components/IpfsImage";
import { showSuccess, showError } from "../components/Dialog";

interface DropRow {
  drop: NftDrop;
  template: AtomicTemplate | null;
}

function dropStatus(drop: NftDrop): "upcoming" | "live" | "ended" | "disabled" {
  if (!drop.enabled) return "disabled";
  const now = Date.now();
  const start = new Date(drop.start_time.endsWith("Z") ? drop.start_time : drop.start_time + "Z").getTime();
  const end = new Date(drop.end_time.endsWith("Z") ? drop.end_time : drop.end_time + "Z").getTime();
  if (now < start) return "upcoming";
  if (now > end) return "ended";
  return "live";
}

export function Drops() {
  const { session, account, connect } = useWallet();
  const [rows, setRows] = useState<DropRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [mintCounts, setMintCounts] = useState<Record<string, number>>({});
  const [whitelisted, setWhitelisted] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listDrops()
      .then(async (drops) => {
        const withTemplates = await Promise.all(
          drops.map(async (drop) => {
            try {
              const template = await getTemplate(drop.collection_name, drop.template_id);
              return { drop, template };
            } catch {
              return { drop, template: null };
            }
          })
        );
        if (!cancelled) setRows(withTemplates);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load drops");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!account) {
      setMintCounts({});
      setWhitelisted({});
      return;
    }
    rows.forEach(({ drop }) => {
      getWalletMintCount(drop.drop_id, account).then((count) =>
        setMintCounts((prev) => ({ ...prev, [drop.drop_id]: count }))
      );
      if (drop.whitelist_enabled) {
        getWhitelistEntry(drop.drop_id, account).then((entry) =>
          setWhitelisted((prev) => ({ ...prev, [drop.drop_id]: !!entry }))
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, rows.length]);

  async function handleMint(drop: NftDrop) {
    if (!session) {
      await connect();
      return;
    }
    setBusyId(drop.drop_id);
    try {
      await mintDrop(session, { dropId: drop.drop_id, price: drop.price });
      logActivity({
        type: "mint_drop",
        account: session.actor.toString(),
        collectionName: drop.collection_name,
        dropId: drop.drop_id,
        templateId: drop.template_id,
        amount: drop.price.quantity,
      });
      showSuccess("Your NFT will appear in My Assets shortly.", "Mint sent");
    } catch (err) {
      showError(err, "Mint failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <h1>Drops</h1>
        <p className="page__subtitle">
          Scheduled mints — some plain, some backed (🔒 badge). Backed drops mint a claim-NFT redeemable for the
          badge amount by burning it later.
        </p>
      </div>

      {loading && <div className="state-message">Loading drops…</div>}
      {error && <div className="state-message state-message--error">{error}</div>}
      {!loading && !error && rows.length === 0 && <div className="state-message">No drops configured yet.</div>}

      <div className="asset-grid">
        {rows.map(({ drop, template }) => {
          const status = dropStatus(drop);
          const isBacked = Number(drop.backing.quantity.split(" ")[0]) > 0;
          const img = (template?.immutable_data?.img as string) || undefined;
          const name = (template?.immutable_data?.name as string) || `Template #${drop.template_id}`;
          const maxSupply = template ? Number(template.max_supply) : 0;
          const issued = template ? Number(template.issued_supply) : drop.minted_count;
          const soldOut = maxSupply > 0 && issued >= maxSupply;
          const walletLimitHit =
            drop.max_per_wallet > 0 && (mintCounts[drop.drop_id] ?? 0) >= drop.max_per_wallet;
          const needsWhitelist = drop.whitelist_enabled && !whitelisted[drop.drop_id];
          const canMint = status === "live" && !soldOut && !walletLimitHit && !needsWhitelist;

          return (
            <div className="asset-card" key={drop.drop_id}>
              <div className="asset-card__media">
                <IpfsImage hash={img} alt={name} loading="lazy" />
                {isBacked && (
                  <span className="backed-badge">🔒 {drop.backing.quantity}</span>
                )}
              </div>
              <div className="asset-card__body">
                <div className="asset-card__collection">{drop.collection_name}</div>
                <div className="asset-card__name">{name}</div>
                <div className="page__subtitle" style={{ margin: "4px 0" }}>
                  {status === "live" && "Live now"}
                  {status === "upcoming" && `Starts ${new Date(drop.start_time).toLocaleString()}`}
                  {status === "ended" && "Ended"}
                  {status === "disabled" && "Paused by creator"}
                  {maxSupply > 0 && ` · ${issued}/${maxSupply} minted`}
                  {drop.whitelist_enabled && " · whitelist"}
                  {drop.max_per_wallet > 0 && ` · limit ${drop.max_per_wallet}/wallet`}
                </div>
                <div className="asset-card__footer">
                  <span className="asset-card__price">{drop.price.quantity}</span>
                  <button
                    className="btn btn--small"
                    disabled={!canMint || busyId === drop.drop_id}
                    onClick={() => handleMint(drop)}
                  >
                    {busyId === drop.drop_id
                      ? "Minting…"
                      : soldOut
                      ? "Sold out"
                      : needsWhitelist
                      ? "Not whitelisted"
                      : walletLimitHit
                      ? "Limit reached"
                      : status === "upcoming"
                      ? "Not started"
                      : status === "ended"
                      ? "Ended"
                      : status === "disabled"
                      ? "Paused"
                      : "Mint"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
